import { type FC, type ChangeEvent } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { BsPlus, BsX } from 'react-icons/bs';

import { updateValidation } from '@editor2/services/elementValidationsActionsService';
import {
	type ResponseBoard,
	type SAQuestion,
	type SAQuestionValidation
} from '@editor2/types/pageElements';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { type FormControlElement } from '@editor2/types/formControl';

interface Props {
	element: SAQuestion | ResponseBoard;
	validation: SAQuestionValidation;
	validationIndex: number;
	courseId: number;
}

export const ValidationRejoinders: FC<Props> = (props) => {
	const { element, validation, validationIndex, courseId } = props;
	const dispatch = useDispatch();
	const debouncedDispatch = useDebouncedDispatch();

	const handleDeleteRejoinderClick = (validationIndex: number, rejoinderIndex: number) => {
		const validation = element.validations[validationIndex];
		const updatedValidation = {
			...validation,
			rejoinders: [
				...validation.rejoinders.slice(0, rejoinderIndex),
				...validation.rejoinders.slice(rejoinderIndex + 1)
			]
		};

		dispatch(updateValidation(element, updatedValidation, validationIndex, courseId));
	};

	const handleAddRejoinderClick = (validation: SAQuestionValidation, validationIndex: number) => {
		const updatedValidation = {
			...validation,
			rejoinders: [...validation.rejoinders, '']
		};

		dispatch(updateValidation(element, updatedValidation, validationIndex, courseId));
	};

	const handleChange = (e: ChangeEvent<FormControlElement>, rejoinderIndex: number) => {
		debouncedDispatch(
			updateValidation(
				element,
				{
					...validation,
					rejoinders: [
						...validation.rejoinders.slice(0, rejoinderIndex),
						e.target.value,
						...validation.rejoinders.slice(rejoinderIndex + 1)
					]
				},
				validationIndex,
				courseId
			)
		);
	};

	return (
		<>
			{validation.enabled ? (
				validation.rejoinders.map((rejoinder, rejoinderIndex) => (
					<div key={rejoinderIndex}>
						<Form.Label className="rejoinderHeader">
							{rejoinderIndex === 0 ? 'Initial rejoinder' : 'Next rejoinder'}
						</Form.Label>

						<Form.Group className="rejoinder">
							<Form.Control
								as="textarea"
								rows={2}
								defaultValue={rejoinder}
								placeholder="Rejoinder"
								className="rejoinder-text"
								onChange={(e) => handleChange(e, rejoinderIndex)}
								disabled={!validation.element_override}
							/>

							{validation.element_override && rejoinderIndex !== 0 && (
								<BsX
									className="deleteRejoinderIcon"
									onClick={() => handleDeleteRejoinderClick(validationIndex, rejoinderIndex)}
								/>
							)}
						</Form.Group>
					</div>
				))
			) : (
				<Form.Label className="rejoinderHeader">
					Initial rejoinder: <span>[N/A]</span>
				</Form.Label>
			)}
			{validation.element_override && validation.enabled && (
				<Button
					size="sm"
					variant="outline-secondary"
					onClick={() => handleAddRejoinderClick(validation, validationIndex)}
					className="addRejoinderButton">
					<BsPlus />
					Add rejoinder
				</Button>
			)}
		</>
	);
};
